import { ArrowLeft, Check, ChevronRight, Monitor, Moon, Sun } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { setSetting } from "../db/actions";
import { useQuery } from "../db/client";
import { PALETTES, type Palette, type PaletteId } from "./palettes";
import { usePrefs, type Theme } from "./prefs";

/*
 * Settings > Appearance: Light / Dark / System (per device) and the colour theme (Evernote, Apple Notes,
 * Notion, Bear, Obsidian, Engram's own and three more; picked on its own page, /settings/theme). A click applies at once; the theme is saved on the
 * device (instant next start, see public/theme-boot.js) and synced as setting `appearance.palette`.
 */

/** Engram's own palette lives in theme.css; these are just its colours for the preview card. */
const ENGRAM = {
  light: { sidebar: "#eee5d8", list: "#f5eee3", paper: "#fbf5ec", line: "#e3d9cb", ink: "#211c18", muted: "#6f665e", create: "#d97757", createInk: "#211c18", accentLine: "#6b78e0", task: "#8a4fd0", scratch: "#fbf2d5" },
  dark: { sidebar: "#232120", list: "#232120", paper: "#1c1814", line: "#34312e", ink: "#f4eee7", muted: "#a89f96", create: "#e0876a", createInk: "#1c1814", accentLine: "#4a58d0", task: "#c08cf0", scratch: "#2d2a1b" },
};

function useDark(theme: Theme) {
  const q = "(prefers-color-scheme: dark)";
  const [sys, setSys] = useState(() => matchMedia(q).matches);
  useEffect(() => {
    const m = matchMedia(q);
    const on = () => setSys(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return theme === "dark" || (theme === "system" && sys);
}

function Mock({ p, dark }: { p: Palette; dark: boolean }) {
  const t = tones(p, dark);
  return (
    <div className="theme-mock" style={{ background: t.list, borderColor: t.line }} aria-hidden>
      <div className="tm-side" style={{ background: t.sidebar, borderColor: t.line }}>
        <span className="tm-pill" style={{ background: t.create, color: t.createInk }}>+ Note</span>
        <span className="tm-row" style={{ background: t.muted }} />
        <span className="tm-row short" style={{ background: t.muted }} />
        <span className="tm-row" style={{ background: t.muted }} />
      </div>
      <div className="tm-main">
        <div className="tm-cards">
          <span className="tm-card sel" style={{ background: t.paper, borderColor: t.accentLine }}>
            <i style={{ background: t.ink }} />
            <i className="short" style={{ background: t.muted }} />
          </span>
          <span className="tm-card" style={{ background: t.paper, borderColor: t.line }}>
            <i style={{ background: t.ink }} />
            <b style={{ borderColor: t.task }} />
          </span>
        </div>
        <span className="tm-scratch" style={{ background: t.scratch }} />
      </div>
    </div>
  );
}

/** Picks the colour theme: saved on this device at once and synced, so every device and every restart keeps it. */
export function pickPalette(id: PaletteId) {
  usePrefs.getState().set({ palette: id });
  void setSetting("appearance.palette", id);
}

/**
 * Keeps this device's theme in step with the synced choice: a pick on another device (or a fresh install)
 * arrives with the next sync; a choice made before syncing existed is uploaded once.
 */
export function usePaletteSync() {
  const row = useQuery<{ value: string }>("SELECT value FROM settings WHERE id = 'appearance.palette' AND deleted = 0", [], ["settings"]);
  useEffect(() => {
    if (!row) return; // replica still loading
    const local = usePrefs.getState().palette;
    const synced = row[0] ? (JSON.parse(row[0].value) as PaletteId) : null;
    if (synced && PALETTES.some((p) => p.id === synced)) {
      if (synced !== local) usePrefs.getState().set({ palette: synced });
    } else if (local !== "engram") void setSetting("appearance.palette", local);
  }, [row]);
}

/** usePaletteSync as its own component, so a settings change re-renders nothing else. */
export function PaletteSync() {
  usePaletteSync();
  return null;
}

/** The palette's own colours (Engram's from theme.css), for the mock cards and the swatch. */
function tones(p: Palette, dark: boolean) {
  return p.id === "engram" ? ENGRAM[dark ? "dark" : "light"] : (dark ? p.dark : p.light)!;
}

/** A small swatch of a theme: its sidebar beside its paper, with the create colour and the selection line. */
function Swatch({ p, dark }: { p: Palette; dark: boolean }) {
  const t = tones(p, dark);
  return (
    <span className="theme-swatch" style={{ borderColor: t.line, background: t.paper }} aria-hidden>
      <span style={{ background: t.sidebar, borderColor: t.line }} />
      <i style={{ background: t.create }} />
      <b style={{ borderColor: t.accentLine }} />
    </span>
  );
}

/**
 * Settings > Appearance: Light / Dark / System, and one row for the colour theme (its name and a swatch);
 * the cards are a level deeper, on /settings/theme (owner, 12 Oct: the grid took too much of the page).
 */
export function AppearanceSettings() {
  const { theme, palette, set } = usePrefs();
  const dark = useDark(theme);
  const [, navigate] = useLocation();
  const current = PALETTES.find((p) => p.id === palette) ?? PALETTES[0]!;
  const modes: [Theme, string, React.ReactNode][] = [
    ["system", "System", <Monitor size={14} key="s" />],
    ["light", "Light", <Sun size={14} key="l" />],
    ["dark", "Dark", <Moon size={14} key="d" />],
  ];
  return (
    <>
      <div className="settings-row">
        <div>
          <div className="settings-label">Mode</div>
          <div className="settings-hint">System follows your device's light or dark setting.</div>
        </div>
        <div className="settings-seg" role="radiogroup" aria-label="Light or dark">
          {modes.map(([id, label, icon]) => (
            <button key={id} role="radio" aria-checked={theme === id} className={theme === id ? "on" : ""} onClick={() => set({ theme: id })}>
              {icon} {label}
            </button>
          ))}
        </div>
      </div>
      <button className="settings-row clickable" onClick={() => navigate("/settings/theme", { state: { fromSettings: true } })}>
        <div>
          <div className="settings-label">Color theme</div>
          <div className="settings-hint">Kept on every device and every time you open Engram.</div>
        </div>
        <span className="settings-value">
          <Swatch p={current} dark={dark} />
          {current.name}
        </span>
        <ChevronRight size={16} className="muted" />
      </button>
    </>
  );
}

/** Settings > Color theme (/settings/theme): the theme cards, each a small mock of that theme. */
export function ColorThemePage() {
  const { theme, palette } = usePrefs();
  const dark = useDark(theme);
  const [, navigate] = useLocation();
  // Back returns to Settings: through history when we came from there (keeps its scroll), else replacing this page.
  const back = () => ((history.state as { fromSettings?: boolean } | null)?.fromSettings ? history.back() : navigate("/settings", { replace: true }));
  return (
    <div className="page settings-page">
      <div className="page-header row settings-sub-header">
        <button className="icon-btn" aria-label="Back to Settings" title="Back to Settings" onClick={back}>
          <ArrowLeft size={20} />
        </button>
        <h1>Color theme</h1>
      </div>
      <div className="settings-hint settings-theme-head">Kept on every device and every time you open Engram, until you change it.</div>
      <div className="theme-grid" role="radiogroup" aria-label="Color theme">
        {PALETTES.map((p) => (
          <button key={p.id} role="radio" aria-checked={palette === p.id} className={`theme-card${palette === p.id ? " on" : ""}`} onClick={() => pickPalette(p.id)}>
            <Mock p={p} dark={dark} />
            <span className="theme-name">
              {p.name}
              {palette === p.id && <Check size={14} />}
            </span>
            <span className="theme-note">{p.note}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
